// Phase 1 minimal error boundary. Catches render-time crashes and
// renders a fallback that keeps the operator on a sensible surface
// instead of a blank white page.
//
// Plan madde 8.3 hardens this:
//   - Map crash → spec 58 envelope shape with classification=internal
//   - Emit to GlitchTip / Sentry browser SDK (plan madde 12.2)
//   - Forward the trace_id so the crash correlates with backend spans
//   - Use mapErrorToToast for non-fatal recoverable errors
//
// React 19's `react-error-boundary` would be a strict upgrade once
// plan madde 8.3 lands; today we keep the dependency surface minimal.

import { Component, type ErrorInfo, type ReactNode } from "react";

import { i18n } from "@/i18n";

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Plan madde 12.2 replaces this with GlitchTip emit. Console at
    // Phase 1 keeps the trace visible to operators running locally.
    // Avoid logging arbitrary string concatenation that could surface
    // PII; React's componentStack is structural, not data.
    console.error("operator-console ErrorBoundary caught:", error, info.componentStack);
  }

  reset = (): void => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  override render(): ReactNode {
    if (this.state.error) {
      const t = i18n.t.bind(i18n);
      return (
        <div
          role="alert"
          className="flex min-h-screen flex-col items-center justify-center gap-3 p-6"
        >
          <h1 className="text-lg font-semibold text-[var(--color-foreground)]">
            {t("errors.boundary_title")}
          </h1>
          <p className="max-w-md text-center text-sm text-[var(--color-muted-foreground)]">
            {t("errors.boundary_body")}
          </p>
          <button
            type="button"
            onClick={this.reset}
            className="rounded-md border border-[var(--color-border)] px-3 py-1 text-xs hover:bg-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          >
            {t("errors.boundary_retry")}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
